<template>
  <div
    :style="sizeStyle"
    :class="[classes.wrapper, classes[layoutClass], 'background']"
    ref="wrapper"
  >
    <transition mode="out-in" name="fade">
      <span
        v-show="displayOverlay"
        :class="[classes.overlay, 'overlay']"
        @click="displayOverlay = !displayOverlay"
      />
    </transition>

    <slot />
  </div>
</template>

<script lang="ts">
import {
  useDimensionSync,
  syncLayoutDependencies
} from "@fresh8/vue-utils/src/dimensionSync";
import { defineComponent, computed, ref, watchEffect } from "vue";
import { createUseStyles } from "@fresh8/vue-jss/src";
import { injectBetData } from "@fresh8/ad-data-composition/src/betData";

import {
  injectBrand,
  injectAdTag
} from "@fresh8/ad-data-composition/src/brand";
import { injectRuntimeConfig } from "@fresh8/ad-data-composition/src/runtimeConfig";
import { provideDisplayOverlay } from "../lib/provideInjectors";
import { provideLayout, useLayout } from "../hooks/useLayout";
import {
  MOBILE_LEADERBOARD,
  provideTemplateSize,
  SMALL_RESPONSIVE,
  LEADERBOARD,
  useTemplateSize
} from "../hooks/useTemplateSize";

import { provideAdSize } from "../hooks/useAdSize";
import { camelize } from "@fresh8/ad-data-composition/src/lib/formatText";

const useStyles = createUseStyles({
  wrapper: {
    backgroundColor: props => props.backgroundColor || "",
    borderRadius: props => props.borderRadius || 0,
    backgroundImage: props =>
      props.backgroundImage && `url(${props.backgroundImage})`,
    padding: "5px 8px",
    boxSizing: "border-box",
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    position: "relative"
  },
  overlay: {
    position: "absolute",
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    zIndex: 900,
    background: "rgba(0,0,0,0.6)"
  },
  [SMALL_RESPONSIVE.name]: {
    padding: "2px 5px 1px"
  },
  [camelize(MOBILE_LEADERBOARD.name)]: {
    padding: 0,
    borderRadius: 0
  },
  [camelize(SMALL_RESPONSIVE.name)]: {
    padding: "2px 5px 1px"
  },
  [LEADERBOARD.name]: {
    padding: 0
  }
});

export default defineComponent({
  setup() {
    const displayOverlay = ref(false);
    const wrapper = ref(null);
    const rc = injectRuntimeConfig();
    const { adTemplateBranding, backgroundImageSets } = injectBrand();
    const adTag = injectAdTag();
    const { adTagSize } = injectAdTag();

    const { dimensions } = useDimensionSync(wrapper);
    const layoutContext = useLayout(dimensions);
    const templateSizeContext = useTemplateSize(adTag);
    const betData = injectBetData();

    const uniqueSports = computed(() => {
      const sports = new Set<string>();
      if (betData?.events?.value) {
        Object.keys(betData.events.value).forEach(key => {
          sports.add(betData.events.value[key].sport);
        });
      }
      return sports;
    });

    provideLayout(layoutContext);
    provideTemplateSize(templateSizeContext);
    provideAdSize(dimensions);

    watchEffect(onInvalidate => {
      let updateParentToken;
      function updateParent() {
        window.parent.postMessage(
          { type: "f8_size", contentHeight: dimensions.value.height },
          "*"
        );

        updateParentToken = setTimeout(updateParent, 100);
      }

      rc.value.globalEmit?.("adResize", {
        height: dimensions.value.height,
        width: dimensions.value.width
      });

      updateParent();
      onInvalidate(() => updateParentToken && clearTimeout(updateParentToken));
    });

    const size = computed(() => {
      rc.value.globalEmit?.("adResize", {
        height: dimensions.value.height,
        width: dimensions.value.width
      });

      const height =
        adTag.adTagSize.value?.height && `${adTag.adTagSize.value?.height}px`;

      if (adTag.responsive.value) {
        return {
          width: "100%",
          height: height,
          minHeight: !height && "270px"
        };
      }

      return {
        width: `${adTag.adTagSize.value?.width}px`,
        height: height,
        _id: adTagSize.value._id
      };
    });

    syncLayoutDependencies([
      computed(() => size.value?.width),
      computed(() => size.value?.height),
      adTag.responsive
    ]);

    const backgroundImage = computed(() => {
      if (typeof backgroundImageSets.value?.preview === "object") {
        return (
          backgroundImageSets.value?.preview?.userAssets?.find(
            asset => asset.adSlotSize === size.value._id
          )?.binary || ""
        );
      }

      if (uniqueSports?.value.size === 1) {
        const sport: string = uniqueSports.value
          .values()
          .next()
          .value?.toLowerCase()
          .replace(" ", "-");

        if (sport && typeof backgroundImageSets.value?.[sport] === "object") {
          return (
            backgroundImageSets.value[sport].userAssets.find(
              asset => asset.adSlotSize === size.value._id
            )?.binary || ""
          );
        }
      }
      return (
        (typeof backgroundImageSets.value?.default === "object" &&
          backgroundImageSets.value?.default?.userAssets?.find(
            asset => asset.adSlotSize === size.value._id
          )?.binary) ||
        ""
      );
    });

    const reativeStyles = computed(() => ({
      ...size.value,
      ...adTemplateBranding.value,
      backgroundImage: backgroundImage.value
    }));

    const classesRef = useStyles(reativeStyles);
    provideDisplayOverlay(displayOverlay);

    return {
      classes: classesRef,
      wrapper,
      sizeStyle: size,
      displayOverlay,
      layoutClass: computed(() => camelize(templateSizeContext.size.value.name))
    };
  }
});
</script>
